/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { db, auth } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { useTheme, alpha } from "@mui/material/styles";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import laborRulesByState from '../data/laborRulesByState.json';

export default function LocationsManager({
  selectedLocationId,
  onSelectLocation,
  onDeleteLocation,
  onAddLocation,
  childrenAfterSelector = null,
}) {
  const theme = useTheme();
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState("");
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingOpen, setEditingOpen] = useState(false);
  const [editingName, setEditingName] = useState("");
  // Location detail form state for the selected location
  const [detailName, setDetailName] = useState("");
  const [detailAddress, setDetailAddress] = useState("");
  // store IANA timezone identifiers (e.g. America/New_York)
  const [detailTimezone, setDetailTimezone] = useState('America/New_York');
  const [detailEndOfBusiness, setDetailEndOfBusiness] = useState('24:00');
  const [savingDetails, setSavingDetails] = useState(false);
  // Scheduling hours state
  const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const [schedulingHours, setSchedulingHours] = useState({});
  const [editHoursOpen, setEditHoursOpen] = useState(false);
  const [editDays, setEditDays] = useState([]);
  const [editStart, setEditStart] = useState('06:00');
  const [editEnd, setEditEnd] = useState('00:00');
  const [editClosed, setEditClosed] = useState(false);

  // generate 15-minute time options (00:00 - 23:45)
  const generateTimeOptions = () => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        const value = `${hh}:${mm}`;
        const dt = new Date();
        dt.setHours(h, m, 0, 0);
        const label = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        opts.push({ value, label });
      }
    }
    return opts;
  };
  const TIME_OPTIONS = generateTimeOptions();
  // Shift feedback settings
  const [shiftFeedbackEnabled, setShiftFeedbackEnabled] = useState(false);
  const [shiftFeedbackMinutes, setShiftFeedbackMinutes] = useState(15);
  const [shiftFeedbackWhen, setShiftFeedbackWhen] = useState('After');
  const [shiftFeedbackDays, setShiftFeedbackDays] = useState([]);
  // Holiday pay rates
  const [holidayPayRates, setHolidayPayRates] = useState([]);
  const [newHolidayName, setNewHolidayName] = useState('');
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayMultiplier, setNewHolidayMultiplier] = useState(1.5);
  const [newHolidayRecurring, setNewHolidayRecurring] = useState(false);
  const [addHolidayOpen, setAddHolidayOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addDate, setAddDate] = useState('');
  const [addMultiplier, setAddMultiplier] = useState(1.5);
  const [addRecurring, setAddRecurring] = useState(false);
  // Country (preset) holidays
  const PRESET_HOLIDAYS = [
    { id: 'new_year', title: "January 1", subtitle: "New Year's Day" },
    { id: 'mlk', title: "Third Monday of January", subtitle: "Martin Luther King, Jr. Day" },
    { id: 'wash_bd', title: "Third Monday of February", subtitle: "Washington's Birthday" },
    { id: 'memorial', title: "Last Monday of May", subtitle: "Memorial Day" },
    { id: 'independence', title: "July 4", subtitle: "Independence Day" },
    { id: 'labor', title: "First Monday of September", subtitle: "Labor Day" },
    { id: 'columbus', title: "Second Monday of October", subtitle: "Columbus Day" },
    { id: 'veterans', title: "November 11", subtitle: "Veterans Day" },
    { id: 'thanksgiving', title: "Fourth Thursday of November", subtitle: "Thanksgiving" },
    { id: 'christmas', title: "December 25", subtitle: "Christmas" },
    { id: 'juneteenth', title: "June 19", subtitle: "Juneteenth" }
  ];
  const [countryHolidaysSettings, setCountryHolidaysSettings] = useState({});
  const [editPresetHolidayOpen, setEditPresetHolidayOpen] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editingPresetMultiplier, setEditingPresetMultiplier] = useState(1.5);
  const [holidayRatesExpanded, setHolidayRatesExpanded] = useState(false);
  const [schedulingHoursExpanded, setSchedulingHoursExpanded] = useState(false);
  const [laborExpanded, setLaborExpanded] = useState(false);
  const [shiftFeedbackExpanded, setShiftFeedbackExpanded] = useState(false);
  // Labor & overtime state for per-location settings
  const [locJurisdiction, setLocJurisdiction] = useState('Outside of USA');
  const [locMinimumWage, setLocMinimumWage] = useState('15.00');
  const [locDailyOvertimeEnabled, setLocDailyOvertimeEnabled] = useState(true);
  const [locDailyDoubleOvertimeEnabled, setLocDailyDoubleOvertimeEnabled] = useState(true);
  const [locWeeklyOvertimeEnabled, setLocWeeklyOvertimeEnabled] = useState(true);
  const [locConsecutiveWorkOvertimeEnabled, setLocConsecutiveWorkOvertimeEnabled] = useState(true);
  const [locDailyOvertimeHours, setLocDailyOvertimeHours] = useState(8);
  const [locDailyOvertimeMultiplier, setLocDailyOvertimeMultiplier] = useState(1.5);
  const [locDailyDoubleOvertimeHours, setLocDailyDoubleOvertimeHours] = useState(12);
  const [locDailyDoubleOvertimeMultiplier, setLocDailyDoubleOvertimeMultiplier] = useState(2);
  const [locWeeklyOvertimeHours, setLocWeeklyOvertimeHours] = useState(40);
  const [locWeeklyOvertimeMultiplier, setLocWeeklyOvertimeMultiplier] = useState(1.5);
  const [locConsecutiveWorkDays, setLocConsecutiveWorkDays] = useState(7);
  const [locConsecutiveWorkMultiplier, setLocConsecutiveWorkMultiplier] = useState(1.5);
  const [locOvertimePayRateMethod, setLocOvertimePayRateMethod] = useState('Hours Worked');
  const [locSplitHoursOnHolidays, setLocSplitHoursOnHolidays] = useState(false);
  const [locUseStateMinimumForOvertime, setLocUseStateMinimumForOvertime] = useState(false);

  const formatBusinessDayCutoff = (cutoff) => {
    if (!cutoff || typeof cutoff !== 'object') return null;
    const hour = Number(cutoff.hour);
    const minute = Number(cutoff.minute);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (hour === 24 && minute === 0) return '24:00';
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const parseEndOfBusiness = (value) => {
    if (!value || typeof value !== 'string') return { hour: 24, minute: 0 };
    if (value === '24:00') return { hour: 24, minute: 0 };
    const [hhRaw, mmRaw] = value.split(':');
    const hour = Number(hhRaw);
    const minute = Number(mmRaw);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return { hour: 24, minute: 0 };
    return {
      hour: Math.max(0, Math.min(23, hour)),
      minute: Math.max(0, Math.min(59, minute))
    };
  };

  // Apply labor rules for a jurisdiction at the location level
  const applyLaborRulesForStateLoc = (stateName) => {
    if (!stateName) return;
    const rule = Object.values(laborRulesByState).find(r => r.state === stateName);
    if (!rule) return;

    const hasDaily = rule.dailyOT != null;
    setLocDailyOvertimeEnabled(hasDaily);
    setLocDailyOvertimeHours(hasDaily ? rule.dailyOT : 0);
    setLocDailyOvertimeMultiplier(rule.dailyOTMultiplier ?? locDailyOvertimeMultiplier);

    const hasDouble = rule.dailyDoubleOT != null;
    setLocDailyDoubleOvertimeEnabled(hasDouble);
    setLocDailyDoubleOvertimeHours(hasDouble ? rule.dailyDoubleOT : 0);
    setLocDailyDoubleOvertimeMultiplier(rule.dailyDoubleMultiplier ?? locDailyDoubleOvertimeMultiplier);

    const hasWeekly = rule.weeklyOT != null;
    setLocWeeklyOvertimeEnabled(hasWeekly);
    setLocWeeklyOvertimeHours(hasWeekly ? rule.weeklyOT : 0);
    setLocWeeklyOvertimeMultiplier(rule.weeklyOTMultiplier ?? locWeeklyOvertimeMultiplier);

    const hasConsec = rule.consecutiveDays != null;
    setLocConsecutiveWorkOvertimeEnabled(hasConsec);
    setLocConsecutiveWorkDays(hasConsec ? rule.consecutiveDays : 0);
    setLocConsecutiveWorkMultiplier(rule.consecutiveDaysMultiplier ?? locConsecutiveWorkMultiplier);

    setLocUseStateMinimumForOvertime(!!rule.useStateMinimum);
    if (rule.defaultMinWage != null) setLocMinimumWage(String(rule.defaultMinWage));
  };

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    (async () => {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for LocationsManager', e);
          return;
        }
      }
      if (!tenantId) return;

      try {
        const q = query(collection(db, `tenants/${tenantId}/locations`), orderBy('name'));
        unsub = onSnapshot(q, snap => {
          if (!mounted) return;
    const locs = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => !l.archived);
          setLocations(locs);
          // Always auto-select the first location if none is selected or selected is missing
          if (locs.length) {
            if (!selectedLocationId || !locs.some(l => l.id === selectedLocationId)) {
              onSelectLocation(locs[0].id);
            }
          } else {
            onSelectLocation(null);
          }
        }, err => {
          console.warn('locations listener error', err);
          setLocations([]);
          onSelectLocation(null);
        });
      } catch (e) {
        console.warn('Failed to attach locations listener', e);
      }
    })();

    return () => { mounted = false; try { if (typeof unsub === 'function') unsub(); } catch (e) {} };
    // eslint-disable-next-line
  }, [selectedLocationId, onSelectLocation]);

  // When the selected location changes, populate the detail form
  useEffect(() => {
    if (!selectedLocationId) {
      setDetailName("");
      setDetailAddress("");
      return;
    }
    const loc = locations.find(l => l.id === selectedLocationId);
    if (loc) {
      setDetailName(loc.name || "");
      setDetailAddress(loc.address || loc.street || "");
  // prefer stored IANA identifier; fall back to America/New_York
  setDetailTimezone(loc.timezone || 'America/New_York');
  setDetailEndOfBusiness(formatBusinessDayCutoff(loc.businessDayCutoff) || loc.endOfBusiness || '24:00');
  setSchedulingHours(loc.schedulingHours || {});
  setHolidayPayRates(loc.holidayPayRates || []);
  setCountryHolidaysSettings(loc.countryHolidays || {});
  // Shift feedback defaults
  setShiftFeedbackEnabled(!!(loc.shiftFeedback && loc.shiftFeedback.enabled));
  setShiftFeedbackMinutes((loc.shiftFeedback && (typeof loc.shiftFeedback.minutes !== 'undefined')) ? loc.shiftFeedback.minutes : 15);
  setShiftFeedbackWhen((loc.shiftFeedback && loc.shiftFeedback.when) || 'After');
  setShiftFeedbackDays((loc.shiftFeedback && loc.shiftFeedback.days) || WEEKDAYS.slice());
    // load per-location labor settings
    const locLabor = loc.labor || {};
    setLocJurisdiction(locLabor.jurisdiction || 'Outside of USA');
    setLocMinimumWage(locLabor.minimumWage || '15.00');
    setLocDailyOvertimeEnabled(locLabor.dailyOvertimeEnabled !== false);
    setLocDailyDoubleOvertimeEnabled(locLabor.dailyDoubleOvertimeEnabled !== false);
    setLocWeeklyOvertimeEnabled(locLabor.weeklyOvertimeEnabled !== false);
    setLocConsecutiveWorkOvertimeEnabled(locLabor.consecutiveWorkOvertimeEnabled !== false);
    setLocDailyOvertimeHours(locLabor.dailyOvertimeHours ?? 8);
    setLocDailyOvertimeMultiplier(locLabor.dailyOvertimeMultiplier ?? 1.5);
    setLocDailyDoubleOvertimeHours(locLabor.dailyDoubleOvertimeHours ?? 12);
    setLocDailyDoubleOvertimeMultiplier(locLabor.dailyDoubleOvertimeMultiplier ?? 2);
    setLocWeeklyOvertimeHours(locLabor.weeklyOvertimeHours ?? 40);
    setLocWeeklyOvertimeMultiplier(locLabor.weeklyOvertimeMultiplier ?? 1.5);
    setLocConsecutiveWorkDays(locLabor.consecutiveWorkDays ?? 7);
    setLocConsecutiveWorkMultiplier(locLabor.consecutiveWorkMultiplier ?? 1.5);
    setLocOvertimePayRateMethod(locLabor.overtimePayRateMethod || 'Hours Worked');
    setLocSplitHoursOnHolidays(!!locLabor.splitHoursOnHolidays);
    setLocUseStateMinimumForOvertime(!!locLabor.useStateMinimumForOvertime);
    } else {
      // If not found in cache, attempt to fetch directly
      (async () => {
        try {
          // Resolve tenant
          let tenantId = null;
          const adminRaw = localStorage.getItem('adminUser');
          if (adminRaw) {
            try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
          }
          if (!tenantId && auth && auth.currentUser) {
            try {
              const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
              const utSnap = await getDoc(utRef);
              if (utSnap.exists()) {
                const ut = utSnap.data();
                tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
              }
            } catch (e) { /* ignore */ }
          }
          if (!tenantId) return;
          const locRef = doc(db, `tenants/${tenantId}/locations`, selectedLocationId);
          const locSnap = await getDoc(locRef);
          if (locSnap.exists()) {
            const data = locSnap.data();
            setDetailName(data.name || "");
            setDetailAddress(data.address || data.street || "");
            setDetailTimezone(data.timezone || 'America/New_York');
            setDetailEndOfBusiness(formatBusinessDayCutoff(data.businessDayCutoff) || data.endOfBusiness || '24:00');
            setSchedulingHours(data.schedulingHours || {});
            setHolidayPayRates(data.holidayPayRates || []);
            setCountryHolidaysSettings(data.countryHolidays || {});
            // Shift feedback defaults
            setShiftFeedbackEnabled(!!(data.shiftFeedback && data.shiftFeedback.enabled));
            setShiftFeedbackMinutes((data.shiftFeedback && (typeof data.shiftFeedback.minutes !== 'undefined')) ? data.shiftFeedback.minutes : 15);
            setShiftFeedbackWhen((data.shiftFeedback && data.shiftFeedback.when) || 'After');
            setShiftFeedbackDays((data.shiftFeedback && data.shiftFeedback.days) || WEEKDAYS.slice());
            // load per-location labor settings
            const dataLabor = data.labor || {};
            setLocJurisdiction(dataLabor.jurisdiction || 'Outside of USA');
            setLocMinimumWage(dataLabor.minimumWage || '15.00');
            setLocDailyOvertimeEnabled(dataLabor.dailyOvertimeEnabled !== false);
            setLocDailyDoubleOvertimeEnabled(dataLabor.dailyDoubleOvertimeEnabled !== false);
            setLocWeeklyOvertimeEnabled(dataLabor.weeklyOvertimeEnabled !== false);
            setLocConsecutiveWorkOvertimeEnabled(dataLabor.consecutiveWorkOvertimeEnabled !== false);
            setLocDailyOvertimeHours(dataLabor.dailyOvertimeHours ?? 8);
            setLocDailyOvertimeMultiplier(dataLabor.dailyOvertimeMultiplier ?? 1.5);
            setLocDailyDoubleOvertimeHours(dataLabor.dailyDoubleOvertimeHours ?? 12);
            setLocDailyDoubleOvertimeMultiplier(dataLabor.dailyDoubleOvertimeMultiplier ?? 2);
            setLocWeeklyOvertimeHours(dataLabor.weeklyOvertimeHours ?? 40);
            setLocWeeklyOvertimeMultiplier(dataLabor.weeklyOvertimeMultiplier ?? 1.5);
            setLocConsecutiveWorkDays(dataLabor.consecutiveWorkDays ?? 7);
            setLocConsecutiveWorkMultiplier(dataLabor.consecutiveWorkMultiplier ?? 1.5);
            setLocOvertimePayRateMethod(dataLabor.overtimePayRateMethod || 'Hours Worked');
            setLocSplitHoursOnHolidays(!!dataLabor.splitHoursOnHolidays);
            setLocUseStateMinimumForOvertime(!!dataLabor.useStateMinimumForOvertime);
          }
        } catch (e) {
          console.warn('Failed to load selected location details', e);
        }
      })();
    }
  }, [selectedLocationId, locations]);

  const handleSaveShiftFeedback = async () => {
    if (!selectedLocationId) return;
    const payload = {
      enabled: !!shiftFeedbackEnabled,
      minutes: Number(shiftFeedbackMinutes) || 0,
      when: shiftFeedbackWhen || 'After',
      days: shiftFeedbackDays || []
    };
    try {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) { console.warn('Could not resolve tenant for shift feedback save', e); }
      }
      if (!tenantId) return;
      await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), { shiftFeedback: payload });
    } catch (e) {
      console.error('Failed to save shift feedback settings', e);
    }
  };

  const handleAdd = async () => {
    if (!newLocation.trim()) return;
    // If parent provided an add handler, use it so parent can select the newly-created location
    if (typeof onAddLocation === 'function') {
      try {
        const ref = await onAddLocation(newLocation.trim());
        setNewLocation("");
        // parent will select the newly-added location if ref provided
        return;
      } catch (e) {
        console.error('onAddLocation failed', e);
        return;
      }
    }
    // Fallback: perform local add (same logic as before)
    try {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for add location', e);
          return;
        }
      }
      if (!tenantId) {
        console.warn('Tenant not found for add location');
        return;
      }
      await addDoc(collection(db, `tenants/${tenantId}/locations`), { name: newLocation.trim() });
      setNewLocation("");
    } catch (e) {
      console.error('Failed to add location', e);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for delete location', e);
          return;
        }
      }
      if (!tenantId) return;
  await updateDoc(doc(db, `tenants/${tenantId}/locations`, id), { archived: true, updatedAt: new Date() });
    } catch (e) {
      console.error('Failed to delete location', e);
    }
  };

  // Called when delete button clicked for a location. If parent provided onDeleteLocation,
  // delegate to it so parent can confirm; otherwise perform local delete.
  const handleDeleteClick = async (location) => {
    if (typeof onDeleteLocation === 'function') {
      try { onDeleteLocation(location); } catch (e) { console.error('onDeleteLocation handler failed', e); }
      return;
    }
    await handleDelete(location.id);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setEditingName(location.name || "");
    setEditingOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingLocation || !editingName.trim()) return;
    try {
      // Resolve tenant (same logic as other handlers)
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for LocationsManager edit', e);
          return;
        }
      }
      if (!tenantId) return;
      await updateDoc(doc(db, `tenants/${tenantId}/locations`, editingLocation.id), { name: editingName.trim() });
      setEditingOpen(false);
      setEditingLocation(null);
      setEditingName("");
    } catch (e) {
      console.error('Failed to update location', e);
    }
  };

  const handleSaveDetails = async () => {
    if (!selectedLocationId) return;
    setSavingDetails(true);
    try {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for saving location details', e);
          setSavingDetails(false);
          return;
        }
      }
      if (!tenantId) {
        console.warn('Tenant not found for saving location details');
        setSavingDetails(false);
        return;
      }

      await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), {
  name: detailName.trim(),
  address: detailAddress.trim(),
  timezone: detailTimezone,
  businessDayCutoff: parseEndOfBusiness(detailEndOfBusiness || '24:00'),
  endOfBusiness: deleteField(),
  labor: {
    jurisdiction: locJurisdiction || 'Outside of USA',
    minimumWage: locMinimumWage || '15.00',
    dailyOvertimeEnabled: !!locDailyOvertimeEnabled,
    dailyDoubleOvertimeEnabled: !!locDailyDoubleOvertimeEnabled,
    weeklyOvertimeEnabled: !!locWeeklyOvertimeEnabled,
    consecutiveWorkOvertimeEnabled: !!locConsecutiveWorkOvertimeEnabled,
    dailyOvertimeHours: locDailyOvertimeHours,
    dailyOvertimeMultiplier: locDailyOvertimeMultiplier,
    dailyDoubleOvertimeHours: locDailyDoubleOvertimeHours,
    dailyDoubleOvertimeMultiplier: locDailyDoubleOvertimeMultiplier,
    weeklyOvertimeHours: locWeeklyOvertimeHours,
    weeklyOvertimeMultiplier: locWeeklyOvertimeMultiplier,
    consecutiveWorkDays: locConsecutiveWorkDays,
    consecutiveWorkMultiplier: locConsecutiveWorkMultiplier,
    overtimePayRateMethod: locOvertimePayRateMethod,
    splitHoursOnHolidays: !!locSplitHoursOnHolidays,
    useStateMinimumForOvertime: !!locUseStateMinimumForOvertime
  },
      });
      // also write schedulingHours state (if present)
      try {
        await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), {
          schedulingHours: schedulingHours || {}
        });
      } catch (e) {
        // non-fatal
        console.warn('Failed to persist schedulingHours with details save', e);
      }
    } catch (e) {
      console.error('Failed to save location details', e);
    } finally {
      setSavingDetails(false);
    }
  };

  const openEditHours = (dayName) => {
    // Preselect a single day when editing from row, or allow multiple selected days from state
    if (dayName) setEditDays([dayName]);
    setEditClosed(false);
    // If day exists in schedulingHours, prefill times
    const dayData = schedulingHours[dayName] || null;
    if (dayData) {
      setEditClosed(dayData.open === false);
      setEditStart(dayData.start || '06:00');
      setEditEnd(dayData.end || '00:00');
    } else {
      setEditStart('06:00');
      setEditEnd('00:00');
    }
    setEditHoursOpen(true);
  };

  const handleSaveHours = async () => {
    if (!selectedLocationId || !editDays.length) return;
    const updated = { ...schedulingHours };
    editDays.forEach(d => {
      if (editClosed) {
        updated[d] = { open: false };
      } else {
        updated[d] = { open: true, start: editStart, end: editEnd };
      }
    });
    setSchedulingHours(updated);
    setEditHoursOpen(false);

    // persist to firestore
    try {
      // Resolve tenant
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for saving scheduling hours', e);
          return;
        }
      }
      if (!tenantId) return;
      await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), { schedulingHours: updated });
    } catch (e) {
      console.error('Failed to save scheduling hours', e);
    }
  };

  const formatTimeDisplay = (t) => {
    if (!t) return '';
    // t is expected HH:MM (24h) or '00:00'
    const [hh, mm] = t.split(':').map(Number);
    const dt = new Date();
    dt.setHours(hh % 24, mm, 0, 0);
    const options = { hour: 'numeric', minute: '2-digit' };
    return dt.toLocaleTimeString([], options);
  };

  const selectedLocation = locations.find((loc) => loc.id === selectedLocationId) || null;
  const selectedLocationSummary = selectedLocation
    ? {
        name: selectedLocation.name || 'Untitled location',
        address: selectedLocation.address || selectedLocation.street || 'Address not set',
      }
    : null;

  return (
    <Box
      component={Paper}
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 4,
        borderRadius: 5,
        boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
        bgcolor: alpha(theme.palette.background.paper, 0.96),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
      }}
    >
      <Box
        sx={{
          p: { xs: 2.25, md: 3 },
          mb: 3,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}
            >
              Location Settings
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.5, mb: 1, fontWeight: 800 }}>
              Locations
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.55 }}>
              Manage store details, holiday pay rules, scheduling hours, and labor settings with one location selected at a time.
            </Typography>
          </Box>
          <Chip
            label={`${locations.length} saved`}
            sx={{
              height: 38,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              fontWeight: 800,
              alignSelf: 'flex-start',
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 420px) auto' },
            alignItems: 'end',
          }}
        >
          <TextField
            label="Add a new location"
            value={newLocation}
            onChange={e => setNewLocation(e.target.value)}
            size="small"
            placeholder="Enter location name"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: alpha(theme.palette.common.white, 0.78),
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              height: 42,
              px: 2.5,
              borderRadius: 999,
              fontWeight: 700,
              boxShadow: 3,
            }}
          >
            Add Location
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '340px minmax(0, 1fr)' } }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 4,
            borderColor: alpha(theme.palette.primary.main, 0.12),
            bgcolor: alpha(theme.palette.background.paper, 0.94),
          }}
        >
          <Box sx={{ mb: 1.75 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Store List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a location to view and edit its configuration.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {locations.map(loc => {
              const isActive = loc.id === selectedLocationId;
              return (
                <Box
                  key={loc.id}
                  onClick={() => onSelectLocation(loc.id)}
                  sx={{
                    p: 1.6,
                    borderRadius: 3,
                    cursor: 'pointer',
                    border: `1px solid ${isActive ? alpha(theme.palette.primary.main, 0.45) : alpha(theme.palette.divider, 0.9)}`,
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.background.default, 0.55),
                    boxShadow: isActive ? `0 10px 24px ${alpha(theme.palette.primary.main, 0.16)}` : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.35),
                      bgcolor: alpha(theme.palette.primary.main, isActive ? 0.1 : 0.05),
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: isActive ? 'primary.main' : 'text.primary' }}>
                        {loc.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {loc.address || loc.street || 'No address on file'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton color="primary" onClick={e => { e.stopPropagation(); openEditDialog(loc); }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={e => { e.stopPropagation(); handleDeleteClick(loc); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 2.25,
            borderRadius: 4,
            borderColor: alpha(theme.palette.primary.main, 0.12),
            bgcolor: alpha(theme.palette.background.paper, 0.94),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {selectedLocationSummary ? selectedLocationSummary.name : 'Select a location'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                {selectedLocationSummary
                  ? selectedLocationSummary.address
                  : 'Pick a saved location on the left to open its details.'}
              </Typography>
            </Box>
            {selectedLocationSummary && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={detailTimezone === 'America/Los_Angeles'
                    ? 'Pacific'
                    : detailTimezone === 'America/Denver'
                    ? 'Mountain'
                    : detailTimezone === 'America/Chicago'
                    ? 'Central'
                    : detailTimezone === 'America/New_York'
                    ? 'Eastern'
                    : detailTimezone}
                  sx={{ borderRadius: 999, fontWeight: 700 }}
                />
                <Chip
                  label={`Business day ends ${formatTimeDisplay(detailEndOfBusiness === '24:00' ? '00:00' : detailEndOfBusiness) || 'Midnight'}`}
                  sx={{
                    borderRadius: 999,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.dark',
                    fontWeight: 700,
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {childrenAfterSelector ? (
        <Box sx={{ mt: 3 }}>
          {childrenAfterSelector}
        </Box>
      ) : null}

      {/* Selected location General settings */}
      {selectedLocationId && (
        <Box
          sx={{
            mt: 3,
            p: { xs: 2.25, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.95),
          }}
        >
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.1em' }}>
              Core Setup
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              General
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Basic identity and timezone settings that affect schedules, reporting, and business-day calculations.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              maxWidth: 900,
            }}
          >
            <TextField label="Location name" value={detailName} onChange={e => setDetailName(e.target.value)} fullWidth />
            <TextField label="Street address" value={detailAddress} onChange={e => setDetailAddress(e.target.value)} fullWidth />

            <FormControl fullWidth size="small">
              <InputLabel>Time zone</InputLabel>
              <Select value={detailTimezone} label="Time zone" onChange={e => setDetailTimezone(e.target.value)}>
                <MenuItem value="America/Los_Angeles">(GMT-08:00) Pacific Time</MenuItem>
                <MenuItem value="America/Denver">(GMT-07:00) Mountain Time</MenuItem>
                <MenuItem value="America/Chicago">(GMT-06:00) Central Time</MenuItem>
                <MenuItem value="America/New_York">(GMT-05:00) Eastern Time</MenuItem>
                <MenuItem value="UTC">(GMT+00:00) UTC</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>End of business day</InputLabel>
              <Select value={detailEndOfBusiness} label="End of business day" onChange={e => setDetailEndOfBusiness(e.target.value)}>
                {/* Allow 15-minute increments from 00:00 - 24:00 */}
                {TIME_OPTIONS.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
                <MenuItem value="24:00">Midnight (24:00)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' }, pt: 0.5 }}>
              <Button variant="contained" onClick={handleSaveDetails} disabled={savingDetails}>
                {savingDetails ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Holiday Pay Rates */}
      {selectedLocationId && (
        <Box
          component={Paper}
          sx={{
            mt: 3,
            p: { xs: 2.25, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.16)}`,
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Box
            sx={{
              mb: holidayRatesExpanded ? 2.5 : 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: 'warning.dark', fontWeight: 800, letterSpacing: '0.1em' }}>
                Holiday Rules
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Holiday Pay Rates</Typography>
              <Typography variant="body2" color="text.secondary">
                Define special pay multipliers for preset and custom holidays at this location.
              </Typography>
            </Box>
            <IconButton
              onClick={() => setHolidayRatesExpanded((open) => !open)}
              sx={{
                border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
                bgcolor: alpha(theme.palette.warning.main, 0.06),
              }}
            >
              {holidayRatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={holidayRatesExpanded}>
          {/* Add button moved below the holiday lists; use modal to add custom holiday */}

          <Box>
            {holidayPayRates.length > 0 && holidayPayRates.map((h, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                  borderRadius: 3,
                  borderColor: alpha(theme.palette.warning.main, 0.18),
                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{h.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{h.recurring ? `${h.date.slice(5)} (recurring)` : h.date} · {h.multiplier}x</Typography>
                </Box>
                <Box>
                  <Button color="error" onClick={async () => {
                    const updated = holidayPayRates.filter((_, i) => i !== idx);
                    setHolidayPayRates(updated);
                    try {
                      let tenantId = null;
                      const adminRaw = localStorage.getItem('adminUser');
                      if (adminRaw) {
                        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
                      }
                      if (!tenantId && auth && auth.currentUser) {
                        try {
                          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
                          const utSnap = await getDoc(utRef);
                          if (utSnap.exists()) {
                            const ut = utSnap.data();
                            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
                          }
                        } catch (e) { console.warn(e); }
                      }
                      if (tenantId) await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), { holidayPayRates: updated });
                    } catch (e) { console.error('Failed to delete holiday rate', e); }
                  }}>Delete</Button>
                </Box>
              </Paper>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5, fontSize: '1.1rem', fontWeight: 800 }}>Country holidays</Typography>
              <Typography variant="body2" color="text.secondary">
                Toggle official holidays on or off and set wage multipliers when needed.
              </Typography>
            </Box>
            <Chip
              label={`${Object.values(countryHolidaysSettings).filter((cfg) => cfg?.enabled).length} enabled`}
              sx={{
                borderRadius: 999,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.dark',
                fontWeight: 700,
              }}
            />
          </Box>
          <Table sx={{ mt: 1, '& .MuiTableCell-root': { borderColor: alpha(theme.palette.divider, 0.75) } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 72 }}></TableCell>
                <TableCell>Holidays</TableCell>
                <TableCell>Hourly wage multiplier</TableCell>
                <TableCell align="right">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PRESET_HOLIDAYS.map(ph => {
                const cfg = countryHolidaysSettings[ph.id] || { enabled: false, multiplier: null };
                return (
                  <TableRow key={ph.id}>
                    <TableCell>
                      <Switch checked={!!cfg.enabled} onChange={async (e) => {
                        const updated = { ...countryHolidaysSettings, [ph.id]: { ...(countryHolidaysSettings[ph.id] || {}), enabled: e.target.checked, multiplier: (countryHolidaysSettings[ph.id] && countryHolidaysSettings[ph.id].multiplier) || 1.5 } };
                        setCountryHolidaysSettings(updated);
                        try {
                          let tenantId = null;
                          const adminRaw = localStorage.getItem('adminUser');
                          if (adminRaw) { try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) {} }
                          if (!tenantId && auth && auth.currentUser) {
                            try { const utRef = doc(db, `userTenants/${auth.currentUser.uid}`); const utSnap = await getDoc(utRef); if (utSnap.exists()) { const ut = utSnap.data(); tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]); } } catch (e) { console.warn(e); }
                          }
                          if (tenantId) await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), { countryHolidays: updated });
                        } catch (e) { console.error('Failed to persist country holiday toggle', e); }
                      }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontWeight: 600 }}>{ph.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{ph.subtitle}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{cfg.enabled ? (cfg.multiplier || 1.5) : '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => { setEditingPresetId(ph.id); setEditingPresetMultiplier((countryHolidaysSettings[ph.id] && countryHolidaysSettings[ph.id].multiplier) || 1.5); setEditPresetHolidayOpen(true); }}>
                        <EditOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Edit preset multiplier dialog */}
          <Dialog open={editPresetHolidayOpen} onClose={() => setEditPresetHolidayOpen(false)}>
            <DialogTitle>Edit holiday multiplier</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 1 }}>{PRESET_HOLIDAYS.find(p=>p.id===editingPresetId)?.title}</Typography>
              <TextField label="Multiplier" type="number" value={editingPresetMultiplier} onChange={e=>setEditingPresetMultiplier(Number(e.target.value))} />
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setEditPresetHolidayOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={async ()=>{
                if (!editingPresetId) return;
                const updated = { ...countryHolidaysSettings, [editingPresetId]: { ...(countryHolidaysSettings[editingPresetId] || {}), multiplier: Number(editingPresetMultiplier) } };
                setCountryHolidaysSettings(updated);
                try {
                  let tenantId = null;
                  const adminRaw = localStorage.getItem('adminUser');
                  if (adminRaw) { try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) {} }
                  if (!tenantId && auth && auth.currentUser) {
                    try { const utRef = doc(db, `userTenants/${auth.currentUser.uid}`); const utSnap = await getDoc(utRef); if (utSnap.exists()) { const ut = utSnap.data(); tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]); } } catch (e) { console.warn(e); }
                  }
                  if (tenantId) await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), { countryHolidays: updated });
                } catch (e) { console.error('Failed to persist preset multiplier', e); }
                setEditPresetHolidayOpen(false);
              }}>Save</Button>
            </DialogActions>
          </Dialog>
        
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
            <Button startIcon={<AddIcon />} onClick={() => setAddHolidayOpen(true)}>Holiday</Button>
          </Box>

          {/* Add custom holiday modal */}
          <Dialog open={addHolidayOpen} onClose={() => setAddHolidayOpen(false)}>
            <DialogTitle>Add custom holiday</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 400 }}>
                <TextField label="Name" value={addName} onChange={e=>setAddName(e.target.value)} fullWidth />
                <RadioGroup value={addRecurring ? 'repeats' : 'specific'} onChange={e=>setAddRecurring(e.target.value === 'repeats')}>
                  <FormControlLabel value="specific" control={<Radio />} label="Specific date" />
                  <FormControlLabel value="repeats" control={<Radio />} label="Repeats" />
                </RadioGroup>
                <TextField label="Date" type="date" value={addDate} onChange={e=>setAddDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Hourly wage multiplier (e.g. 1.5)" type="number" value={addMultiplier} onChange={e=>setAddMultiplier(Number(e.target.value))} />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setAddHolidayOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={async ()=>{
                if (!addName.trim() || (!addDate && !addRecurring)) return;
                const entry = { name: addName.trim(), date: addDate, multiplier: Number(addMultiplier) || 1.5, recurring: !!addRecurring };
                const updated = [...holidayPayRates, entry];
                setHolidayPayRates(updated);
                try {
                  let tenantId = null;
                  const adminRaw = localStorage.getItem('adminUser');
                  if (adminRaw) { try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) {} }
                  if (!tenantId && auth && auth.currentUser) {
                    try { const utRef = doc(db, `userTenants/${auth.currentUser.uid}`); const utSnap = await getDoc(utRef); if (utSnap.exists()) { const ut = utSnap.data(); tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]); } } catch (e) { console.warn(e); }
                  }
                  if (tenantId) await updateDoc(doc(db, `tenants/${tenantId}/locations`, selectedLocationId), { holidayPayRates: updated });
                } catch (e) { console.error('Failed to persist custom holiday', e); }
                setAddName(''); setAddDate(''); setAddMultiplier(1.5); setAddRecurring(false); setAddHolidayOpen(false);
              }}>Save</Button>
            </DialogActions>
          </Dialog>

          </Collapse>
        </Box>
      )}

      {/* Scheduling Hours */}
      {selectedLocationId && (
        <Box
          component={Paper}
          sx={{
            mt: 3,
            p: { xs: 2.25, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
          }}
        >
          <Box
            sx={{
              mb: schedulingHoursExpanded ? 2 : 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: 'info.main', fontWeight: 800, letterSpacing: '0.1em' }}>
                Scheduling Window
              </Typography>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>Scheduling Hours</Typography>
              <Typography variant="body2" color="text.secondary">
                The times below should reflect the earliest and latest time that employees will be scheduled.
              </Typography>
            </Box>
            <IconButton
              onClick={() => setSchedulingHoursExpanded((open) => !open)}
              sx={{
                border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                bgcolor: alpha(theme.palette.info.main, 0.06),
              }}
            >
              {schedulingHoursExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={schedulingHoursExpanded}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {WEEKDAYS.map(d => {
              const isOpen = schedulingHours[d] ? schedulingHours[d].open !== false : true;
              return (
                <Chip key={d} label={d.slice(0,3)} clickable onClick={() => openEditHours(d)} color={isOpen ? 'primary' : 'default'} />
              );
            })}
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell>Start time</TableCell>
                <TableCell>End time</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {WEEKDAYS.map(d => {
                const data = schedulingHours[d] || null;
                return (
                  <TableRow key={d}>
                    <TableCell>{d}</TableCell>
                    <TableCell>{data && data.open !== false ? formatTimeDisplay(data.start || '06:00') : 'Closed'}</TableCell>
                    <TableCell>{data && data.open !== false ? formatTimeDisplay(data.end || '00:00') : 'Closed'}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openEditHours(d)}><EditOutlinedIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Edit scheduling hours modal */}
          <Dialog open={editHoursOpen} onClose={() => setEditHoursOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Edit scheduling hours</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>Days to update</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {WEEKDAYS.map(d => {
                  const selected = editDays.indexOf(d) > -1;
                  return (
                    <Chip
                      key={d}
                      label={d}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      onClick={() => {
                        setEditDays(prev => {
                          if (prev.indexOf(d) > -1) return prev.filter(x => x !== d);
                          return [...prev, d];
                        });
                      }}
                    />
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">Start time</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={editStart} onChange={e => setEditStart(e.target.value)}>
                      {TIME_OPTIONS.map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">End time</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={editEnd} onChange={e => setEditEnd(e.target.value)}>
                      {TIME_OPTIONS.map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <InputLabel shrink>Closed</InputLabel>
                    <Select value={editClosed ? 'closed' : 'open'} onChange={e => setEditClosed(e.target.value === 'closed')}>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditHoursOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveHours}>Save</Button>
            </DialogActions>
          </Dialog>
          </Collapse>
        </Box>
      )}

      {/* Labor & Overtime (per-location) */}
      {selectedLocationId && (
        <Box
          component={Paper}
          sx={{
            mt: 3,
            p: { xs: 2.25, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.error.main, 0.14)}`,
          }}
        >
          <Box
            sx={{
              mb: laborExpanded ? 2 : 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: 'error.main', fontWeight: 800, letterSpacing: '0.1em' }}>
                Compliance Rules
              </Typography>
              <Typography variant="h6" sx={{ mb: 0.75, fontSize: '1rem', fontWeight: 800 }}>Labor & Overtime (this location)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.86rem' }}>
                The option to use overtime conditions to alter labor costs. Toggle each section to enable or disable it.
              </Typography>
            </Box>
            <IconButton
              onClick={() => setLaborExpanded((open) => !open)}
              sx={{
                border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
                bgcolor: alpha(theme.palette.error.main, 0.06),
              }}
            >
              {laborExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={laborExpanded}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', maxWidth: 1200 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
              <FormControl size="small" sx={{ width: 360 }}>
                <InputLabel>Jurisdiction</InputLabel>
                <Select value={locJurisdiction} label="Jurisdiction" onChange={e => { setLocJurisdiction(e.target.value); applyLaborRulesForStateLoc(e.target.value); }}>
                  <MenuItem value="Alabama">Alabama</MenuItem>
                  <MenuItem value="Alaska">Alaska</MenuItem>
                  <MenuItem value="Arizona">Arizona</MenuItem>
                  <MenuItem value="Arkansas">Arkansas</MenuItem>
                  <MenuItem value="California">California</MenuItem>
                  <MenuItem value="Colorado">Colorado</MenuItem>
                  <MenuItem value="Connecticut">Connecticut</MenuItem>
                  <MenuItem value="Delaware">Delaware</MenuItem>
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Hawaii">Hawaii</MenuItem>
                  <MenuItem value="Idaho">Idaho</MenuItem>
                  <MenuItem value="Illinois">Illinois</MenuItem>
                  <MenuItem value="Indiana">Indiana</MenuItem>
                  <MenuItem value="Iowa">Iowa</MenuItem>
                  <MenuItem value="Kansas">Kansas</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Louisiana">Louisiana</MenuItem>
                  <MenuItem value="Maine">Maine</MenuItem>
                  <MenuItem value="Maryland">Maryland</MenuItem>
                  <MenuItem value="Massachusetts">Massachusetts</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                  <MenuItem value="Minnesota">Minnesota</MenuItem>
                  <MenuItem value="Mississippi">Mississippi</MenuItem>
                  <MenuItem value="Missouri">Missouri</MenuItem>
                  <MenuItem value="Montana">Montana</MenuItem>
                  <MenuItem value="Nebraska">Nebraska</MenuItem>
                  <MenuItem value="Nevada">Nevada</MenuItem>
                  <MenuItem value="New Hampshire">New Hampshire</MenuItem>
                  <MenuItem value="New Jersey">New Jersey</MenuItem>
                  <MenuItem value="New Mexico">New Mexico</MenuItem>
                  <MenuItem value="New York">New York</MenuItem>
                  <MenuItem value="North Carolina">North Carolina</MenuItem>
                  <MenuItem value="North Dakota">North Dakota</MenuItem>
                  <MenuItem value="Ohio">Ohio</MenuItem>
                  <MenuItem value="Oklahoma">Oklahoma</MenuItem>
                  <MenuItem value="Oregon">Oregon</MenuItem>
                  <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
                  <MenuItem value="Rhode Island">Rhode Island</MenuItem>
                  <MenuItem value="South Carolina">South Carolina</MenuItem>
                  <MenuItem value="South Dakota">South Dakota</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                  <MenuItem value="Texas">Texas</MenuItem>
                  <MenuItem value="Utah">Utah</MenuItem>
                  <MenuItem value="Vermont">Vermont</MenuItem>
                  <MenuItem value="Virginia">Virginia</MenuItem>
                  <MenuItem value="Washington">Washington</MenuItem>
                  <MenuItem value="West Virginia">West Virginia</MenuItem>
                  <MenuItem value="Wisconsin">Wisconsin</MenuItem>
                  <MenuItem value="Wyoming">Wyoming</MenuItem>
                  <MenuItem value="Outside of USA">Outside of USA</MenuItem>
                </Select>
              </FormControl>
              <TextField label="$" value={locMinimumWage} onChange={e => setLocMinimumWage(e.target.value)} size="small" sx={{ width: 160 }} />
            </Box>
            {/* daily */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                <Checkbox checked={locDailyOvertimeEnabled} onChange={e => setLocDailyOvertimeEnabled(e.target.checked)} sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.98rem' }}>Daily overtime</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>After working over</Typography>
                  <TextField
                    value={locDailyOvertimeHours}
                    onChange={e => setLocDailyOvertimeHours(Number(e.target.value))}
                    type="number"
                    size="small"
                    disabled={!locDailyOvertimeEnabled}
                    sx={{ width: 64 }}
                    inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>hours in a day, an employee will be paid</Typography>
                  <TextField
                    value={locDailyOvertimeMultiplier}
                    onChange={e => setLocDailyOvertimeMultiplier(Number(e.target.value))}
                    type="number"
                    size="small"
                    disabled={!locDailyOvertimeEnabled}
                    sx={{ width: 64 }}
                    inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>times their regular pay.</Typography>
                </Box>
              </Box>
            </Box>

            {/* daily double */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                <Checkbox checked={locDailyDoubleOvertimeEnabled} onChange={e => setLocDailyDoubleOvertimeEnabled(e.target.checked)} sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.98rem' }}>Daily double overtime</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>After working over</Typography>
                  <TextField value={locDailyDoubleOvertimeHours} onChange={e => setLocDailyDoubleOvertimeHours(Number(e.target.value))} type="number" size="small" disabled={!locDailyDoubleOvertimeEnabled} sx={{ width: 64 }} inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }} />
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>hours in a day, an employee will be paid</Typography>
                  <TextField value={locDailyDoubleOvertimeMultiplier} onChange={e => setLocDailyDoubleOvertimeMultiplier(Number(e.target.value))} type="number" size="small" disabled={!locDailyDoubleOvertimeEnabled} sx={{ width: 64 }} inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }} />
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>times their regular pay.</Typography>
                </Box>
              </Box>
            </Box>

            {/* weekly */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                <Checkbox checked={locWeeklyOvertimeEnabled} onChange={e => setLocWeeklyOvertimeEnabled(e.target.checked)} sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.98rem' }}>Weekly overtime</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>After working over</Typography>
                  <TextField value={locWeeklyOvertimeHours} onChange={e => setLocWeeklyOvertimeHours(Number(e.target.value))} type="number" size="small" disabled={!locWeeklyOvertimeEnabled} sx={{ width: 64 }} inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }} />
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>hours in a week, an employee will be paid</Typography>
                  <TextField value={locWeeklyOvertimeMultiplier} onChange={e => setLocWeeklyOvertimeMultiplier(Number(e.target.value))} type="number" size="small" disabled={!locWeeklyOvertimeEnabled} sx={{ width: 64 }} inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }} />
                  <Typography variant="body2" sx={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>times their regular pay.</Typography>
                </Box>
              </Box>
            </Box>

            {/* consecutive */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                <Checkbox checked={locConsecutiveWorkOvertimeEnabled} onChange={e => setLocConsecutiveWorkOvertimeEnabled(e.target.checked)} sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.98rem' }}>Consecutive work day overtime</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.84rem' }}>If an employee works</Typography>
                      <TextField value={locConsecutiveWorkDays} onChange={e => setLocConsecutiveWorkDays(Number(e.target.value))} type="number" size="small" disabled={!locConsecutiveWorkOvertimeEnabled} sx={{ width: 64 }} inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }} />
                      <Typography variant="body2" sx={{ fontSize: '0.84rem' }}>consecutive days in the work week, that employee will earn</Typography>
                      <TextField value={locConsecutiveWorkMultiplier} onChange={e => setLocConsecutiveWorkMultiplier(Number(e.target.value))} type="number" size="small" disabled={!locConsecutiveWorkOvertimeEnabled} sx={{ width: 64 }} inputProps={{ style: { fontSize: '0.85rem', padding: '6px 8px' } }} />
                      <Typography variant="body2" sx={{ fontSize: '0.84rem', maxWidth: 520 }}>times their pay on that day and any subsequent days until they receive a day off.</Typography>
                    </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Overtime at different pay rates</Typography>
              <FormControl sx={{ width: 260, mt: 1 }} size="small">
                <InputLabel>Overtime method</InputLabel>
                <Select value={locOvertimePayRateMethod} label="Overtime method" onChange={e => setLocOvertimePayRateMethod(e.target.value)}>
                  <MenuItem value="Hours Worked">Hours Worked</MenuItem>
                  <MenuItem value="Regular Rate">Regular Rate</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 1, mb: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={locSplitHoursOnHolidays} onChange={e => setLocSplitHoursOnHolidays(e.target.checked)} />}
                label={
                  <>
                    <Typography sx={{ fontWeight: 700 }}>🕒 Split Hours on Holidays</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      When this setting is on, Shiftworks will automatically split any shift that overlaps into a holiday. That means hours worked on the holiday are counted as holiday hours, while the rest stay regular hours.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Example: If someone works 10 PM – 3 AM and the next day is a holiday, Shiftworks will log 2 regular hours and 3 holiday hours. If this setting is off, all 5 hours would show as regular.
                    </Typography>
                  </>
                }
              />
            </Box>

            <Box sx={{ mt: 1, mb: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={locUseStateMinimumForOvertime} onChange={e => setLocUseStateMinimumForOvertime(e.target.checked)} />}
                label={
                  <>
                    <Typography sx={{ fontWeight: 700 }}>💰 Use State Minimum for Overtime</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Turn this on if you want Shiftworks to use your state's minimum wage when calculating overtime for employees who make less than that amount (like tipped team members).
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This ensures overtime pay, regular rate of pay, and total hours are all calculated fairly — automatically.
                    </Typography>
                  </>
                }
              />
            </Box>

            <Box>
              <Button variant="contained" onClick={handleSaveDetails} disabled={savingDetails}>{savingDetails ? 'Saving...' : 'SAVE'}</Button>
            </Box>
          </Box>
          </Collapse>
        </Box>
      )}

      {/* Shift Feedback */}
      {selectedLocationId && (
        <Box
          component={Paper}
          sx={{
            mt: 3,
            p: { xs: 2.25, md: 3 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
          }}
        >
          <Box
            sx={{
              mb: shiftFeedbackExpanded ? 2 : 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: 'success.dark', fontWeight: 800, letterSpacing: '0.1em' }}>
                Team Feedback
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>Shift feedback</Typography>
            </Box>
            <IconButton
              onClick={() => setShiftFeedbackExpanded((open) => !open)}
              sx={{
                border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                bgcolor: alpha(theme.palette.success.main, 0.06),
              }}
            >
              {shiftFeedbackExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={shiftFeedbackExpanded}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              After each shift, invite team members to share quick feedback about their experience — it’s a great way to spot wins, surface issues early, and improve scheduling.
              Please note: under applicable law, employees may be entitled to compensation for time spent completing shift feedback. Shiftworks recommends consulting with your legal counsel or local HR professional for guidance on wage and hour compliance.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField label="When to send shift feedback prompt" type="number" value={shiftFeedbackMinutes} onChange={e=>setShiftFeedbackMinutes(Number(e.target.value))} sx={{ width: 120 }} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>When</InputLabel>
              <Select value={shiftFeedbackWhen} label="When" onChange={e=>setShiftFeedbackWhen(e.target.value)}>
                <MenuItem value="After">After</MenuItem>
                <MenuItem value="Before">Before</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={shiftFeedbackEnabled} onChange={e=>setShiftFeedbackEnabled(e.target.checked)} />} label="Shift feedback enabled" />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 1 }}>Days to ask for shift feedback</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {WEEKDAYS.map(d => {
                const selected = shiftFeedbackDays.indexOf(d) > -1;
                return (
                  <Chip key={d} label={d.slice(0,3)} clickable color={selected ? 'primary' : 'default'} onClick={() => {
                    setShiftFeedbackDays(prev => {
                      if (prev.indexOf(d) > -1) return prev.filter(x => x !== d);
                      return [...prev, d];
                    });
                  }} />
                );
              })}
            </Box>
          </Box>

          <Box>
            <Button variant="contained" onClick={handleSaveShiftFeedback}>Save</Button>
          </Box>
          </Collapse>
        </Box>
      )}

      {/* Edit Location Dialog */}
      <Dialog open={editingOpen} onClose={() => setEditingOpen(false)}>
        <DialogTitle>Edit location</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
